import { useState } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import type { SectionVariant } from '../../api';

// Re-export for convenience
export type { SectionVariant };

interface VariantsListProps {
  variants: SectionVariant[] | string[];
  onChange: (variants: SectionVariant[]) => void;
  readOnly?: boolean;
}

// Normalize variants to object format
function normalizeVariants(variants: SectionVariant[] | string[]): SectionVariant[] {
  if (variants.length === 0) return [];

  // Check if first item is string
  if (typeof variants[0] === 'string') {
    return (variants as string[]).map(id => ({ id, label: id }));
  }

  return variants as SectionVariant[];
}

export function VariantsList({ variants, onChange, readOnly = false }: VariantsListProps) {
  const [newVariantId, setNewVariantId] = useState('');
  const [newVariantLabel, setNewVariantLabel] = useState('');
  const [newVariantStatus, setNewVariantStatus] = useState<'planned' | 'deprecated' | ''>('');
  const [error, setError] = useState<string | null>(null);

  // Normalize to object format for editing
  const normalizedVariants = normalizeVariants(variants);

  const handleAdd = () => {
    setError(null);

    // Validate ID
    if (!newVariantId.trim()) {
      setError('L\'ID variante è obbligatorio');
      return;
    }

    // ID format: lowercase, hyphens, no spaces
    if (!/^[a-z0-9-]+$/.test(newVariantId)) {
      setError('L\'ID variante deve contenere solo lettere minuscole, numeri e trattini');
      return;
    }

    // Check unique ID
    if (normalizedVariants.some(v => v.id === newVariantId)) {
      setError(`L'ID variante "${newVariantId}" esiste già`);
      return;
    }

    // Add new variant
    const newVariant: SectionVariant = {
      id: newVariantId,
      label: newVariantLabel.trim() || newVariantId, // Use ID as label if not provided
      ...(newVariantStatus && { status: newVariantStatus }),
    };

    onChange([...normalizedVariants, newVariant]);

    // Reset form
    setNewVariantId('');
    setNewVariantLabel('');
    setNewVariantStatus('');
  };

  const handleRemove = (id: string) => {
    onChange(normalizedVariants.filter(v => v.id !== id));
  };

  const handleUpdate = (id: string, field: keyof SectionVariant, value: string) => {
    onChange(
      normalizedVariants.map(v =>
        v.id === id
          ? {
              ...v,
              [field]: field === 'status'
                ? (value || undefined)
                : field === 'label'
                  ? (value.trim() || id) // Use ID as fallback if label is empty
                  : value,
            }
          : v
      )
    );
  };

  return (
    <div className="space-y-3">
      {/* Variants List */}
      {normalizedVariants.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Etichetta
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                {!readOnly && (
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {normalizedVariants.map((variant) => (
                <tr key={variant.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-gray-900">{variant.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    {readOnly ? (
                      <span className="text-sm text-gray-900">{variant.label}</span>
                    ) : (
                      <input
                        type="text"
                        value={variant.label}
                        onChange={(e) => handleUpdate(variant.id, 'label', e.target.value)}
                        className="input-sm w-full text-sm"
                        placeholder="Etichetta"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {readOnly ? (
                      <span
                        className={clsx(
                          'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                          variant.status === 'planned' && 'bg-blue-100 text-blue-700',
                          variant.status === 'deprecated' && 'bg-amber-100 text-amber-700',
                          !variant.status && 'text-gray-500'
                        )}
                      >
                        {variant.status || 'Attiva'}
                      </span>
                    ) : (
                      <select
                        value={variant.status || ''}
                        onChange={(e) => handleUpdate(variant.id, 'status', e.target.value)}
                        className="input-sm w-full text-sm"
                      >
                        <option value="">Attiva</option>
                        <option value="planned">Pianificata</option>
                        <option value="deprecated">Deprecata</option>
                      </select>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemove(variant.id)}
                        className="text-red-600 hover:text-red-700 p-1 rounded-md hover:bg-red-50"
                        title="Rimuovi variante"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {normalizedVariants.length === 0 && (
        <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg">
          <p className="text-sm text-gray-500">Nessuna variante definita</p>
          {!readOnly && (
            <p className="text-xs text-gray-400 mt-1">Aggiungi la tua prima variante qui sotto</p>
          )}
        </div>
      )}

      {/* Add Variant Form */}
      {!readOnly && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Aggiungi Variante</h4>

          {/* Error message */}
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-md p-2 flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-12 gap-3">
            {/* ID Field */}
            <div className="col-span-4">
              <label htmlFor="new-variant-id" className="block text-xs font-medium text-gray-600 mb-1">
                ID <span className="text-red-500">*</span>
              </label>
              <input
                id="new-variant-id"
                type="text"
                value={newVariantId}
                onChange={(e) => {
                  setNewVariantId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  setError(null);
                }}
                className="input-sm w-full text-sm"
                placeholder="es., default"
              />
            </div>

            {/* Label Field */}
            <div className="col-span-4">
              <label htmlFor="new-variant-label" className="block text-xs font-medium text-gray-600 mb-1">
                Etichetta
              </label>
              <input
                id="new-variant-label"
                type="text"
                value={newVariantLabel}
                onChange={(e) => setNewVariantLabel(e.target.value)}
                className="input-sm w-full text-sm"
                placeholder="es., Default"
              />
            </div>

            {/* Status Field */}
            <div className="col-span-3">
              <label htmlFor="new-variant-status" className="block text-xs font-medium text-gray-600 mb-1">
                Stato
              </label>
              <select
                id="new-variant-status"
                value={newVariantStatus}
                onChange={(e) => setNewVariantStatus(e.target.value as 'planned' | 'deprecated' | '')}
                className="input-sm w-full text-sm"
              >
                <option value="">Attiva</option>
                <option value="planned">Pianificata</option>
                <option value="deprecated">Deprecata</option>
              </select>
            </div>

            {/* Add Button */}
            <div className="col-span-1 flex items-end">
              <button
                type="button"
                onClick={handleAdd}
                className="btn-sm btn-primary w-full h-[34px]"
                title="Aggiungi variante"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            L'ID deve essere univoco e contenere solo lettere minuscole, numeri e trattini
          </p>
        </div>
      )}
    </div>
  );
}
